import { Cell, Rect, TRANSPARENT, rectFromPoints } from '../core/types';
import { BoxPlanner, LineStyle, styleSet, styleWeight } from '../unicode/boxdrawing';
import { sliceToWidth } from './text';
import {
  CellWrite,
  ProgressStyle,
  Tool,
  ToolEnv,
  ToolStroke,
  WidgetOptions,
  paintCell,
} from './types';

export type { ProgressStyle, WidgetOptions };

/**
 * Terminal UI widgets.
 *
 * These are stamps, not live objects: dragging one out writes real cells that
 * can then be edited like anything else. Borders go through the box planner so
 * a panel dropped next to another panel joins it properly.
 */

export const DEFAULT_WIDGET_OPTIONS: WidgetOptions = {
  kind: 'panel',
  title: 'PANEL',
  label: 'OK',
  lineStyle: 'light',
  progress: {
    full: '█',
    empty: '░',
    capLeft: '[',
    capRight: ']',
    percent: 60,
    showLabel: true,
  },
  columns: ['USER', 'STATUS', 'PING'],
  items: ['Alice', 'Bob', 'Charlie'],
  filled: false,
};

export const PROGRESS_PRESETS: ReadonlyArray<{ name: string; style: Partial<ProgressStyle> }> = [
  { name: 'Blocks', style: { full: '█', empty: '░', capLeft: '', capRight: '' } },
  { name: 'Shaded', style: { full: '▓', empty: '▒', capLeft: '', capRight: '' } },
  { name: 'Bracketed', style: { full: '=', empty: ' ', capLeft: '[', capRight: ']' } },
  { name: 'Dashed', style: { full: '█', empty: '-', capLeft: '[', capRight: ']' } },
  { name: 'Dots', style: { full: '●', empty: '○', capLeft: '', capRight: '' } },
  { name: 'Braille', style: { full: '⣿', empty: '⠀', capLeft: '│', capRight: '│' } },
];

class Stamp {
  private writes = new Map<number, CellWrite>();

  constructor(private readonly env: ToolEnv) {}

  cell(x: number, y: number, make: (existing: Cell) => Cell): void {
    const { doc, selection } = this.env;
    if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return;
    if (!selection.allows(x, y)) return;
    const key = y * doc.width + x;
    const existing = this.writes.get(key)?.cell ?? this.env.read(x, y);
    this.writes.set(key, { x, y, cell: make(existing) });
  }

  char(x: number, y: number, ch: string): void {
    this.cell(x, y, (existing) => paintCell(existing, this.env.brush, ch));
  }

  text(x: number, y: number, s: string, maxWidth?: number): void {
    const str = maxWidth === undefined ? s : sliceToWidth(s, maxWidth);
    [...str].forEach((ch, i) => this.char(x + i, y, ch));
  }

  list(): CellWrite[] {
    return [...this.writes.values()];
  }
}

/** Draws a connected border and returns the interior rectangle. */
function border(stamp: Stamp, env: ToolEnv, r: Rect, style: LineStyle): Rect {
  const planner = new BoxPlanner((x, y) => env.read(x, y).char, style === 'rounded');
  planner.addRect(r.x, r.y, r.width, r.height, styleWeight(style));
  for (const { x, y, char } of planner.resolve()) stamp.char(x, y, char);
  return { x: r.x + 1, y: r.y + 1, width: Math.max(0, r.width - 2), height: Math.max(0, r.height - 2) };
}

function fillInterior(stamp: Stamp, env: ToolEnv, r: Rect): void {
  for (let y = r.y; y < r.y + r.height; y++) {
    for (let x = r.x; x < r.x + r.width; x++) {
      stamp.cell(x, y, (existing) => ({
        char: existing.char === '' ? ' ' : existing.char,
        fg: existing.fg,
        bg: env.brush.bg,
        attrs: existing.attrs,
      }));
    }
  }
}

/** Renders the progress bar text for a given width. */
export function progressBarText(style: ProgressStyle, width: number): string {
  const caps = style.capLeft.length + style.capRight.length;
  const inner = Math.max(0, width - caps);
  const label = style.showLabel ? ` ${Math.round(style.percent)}%` : '';
  const barWidth = Math.max(0, inner - label.length);
  const pct = Math.max(0, Math.min(100, style.percent)) / 100;
  const filled = Math.round(pct * barWidth);
  const bar = style.full.repeat(filled) + style.empty.repeat(Math.max(0, barWidth - filled));
  return `${style.capLeft}${bar}${label}${style.capRight}`;
}

/** Column widths for a table, distributing leftover space to the first column. */
export function tableColumnWidths(columns: readonly string[], totalWidth: number): number[] {
  const n = Math.max(1, columns.length);
  // Each column needs a separator plus one space of padding on each side.
  const usable = totalWidth - (n + 1) - n * 2;
  if (usable <= 0) return columns.map(() => 1);
  const base = Math.floor(usable / n);
  const widths = columns.map(() => base);
  let remainder = usable - base * n;
  for (let i = 0; remainder > 0; i = (i + 1) % n, remainder--) widths[i] += 1;
  return widths;
}

export function renderWidget(env: ToolEnv, r: Rect, opts: WidgetOptions): CellWrite[] {
  const stamp = new Stamp(env);
  const style = opts.lineStyle;
  const set = styleSet(style);

  switch (opts.kind) {
    case 'panel': {
      if (r.width < 2 || r.height < 2) break;
      const inner = border(stamp, env, r, style);
      if (opts.filled) fillInterior(stamp, env, inner);
      if (opts.title) {
        const title = sliceToWidth(` ${opts.title} `, Math.max(0, r.width - 2));
        stamp.text(r.x + 1, r.y, title);
      }
      break;
    }

    case 'titlebar': {
      for (let x = r.x; x < r.x + r.width; x++) {
        stamp.cell(x, r.y, () => ({
          char: ' ',
          fg: env.brush.fg,
          bg: env.brush.bg,
          attrs: env.brush.attrs,
        }));
      }
      stamp.text(r.x + 1, r.y, opts.title, Math.max(0, r.width - 2));
      break;
    }

    case 'separator': {
      const planner = new BoxPlanner((x, y) => env.read(x, y).char, style === 'rounded');
      planner.addHorizontal(r.x, r.x + r.width - 1, r.y, styleWeight(style));
      for (const { x, y, char } of planner.resolve()) stamp.char(x, y, char);
      break;
    }

    case 'progress': {
      stamp.text(r.x, r.y, progressBarText(opts.progress, r.width), r.width);
      break;
    }

    case 'button': {
      const label = ` ${opts.label} `;
      const width = Math.max(r.width, label.length + 2);
      if (r.height >= 3) {
        const inner = border(stamp, env, { ...r, width }, style);
        const cx = inner.x + Math.max(0, Math.floor((inner.width - label.length) / 2));
        stamp.text(cx, inner.y, label, inner.width);
      } else {
        stamp.text(r.x, r.y, `${set.topLeft === '+' ? '[' : '['}${label}]`, width);
      }
      break;
    }

    case 'input': {
      if (r.height >= 3) {
        const inner = border(stamp, env, r, style);
        stamp.text(inner.x + 1, inner.y, opts.label, Math.max(0, inner.width - 1));
      } else {
        stamp.text(r.x, r.y, `› ${opts.label}`, r.width);
        for (let x = r.x + 2 + opts.label.length; x < r.x + r.width; x++) stamp.char(x, r.y, '▁');
      }
      break;
    }

    case 'list': {
      const inner = r.height >= 3 && r.width >= 3 ? border(stamp, env, r, style) : r;
      opts.items.forEach((item, i) => {
        if (i >= inner.height) return;
        stamp.text(inner.x + 1, inner.y + i, `• ${item}`, Math.max(0, inner.width - 1));
      });
      break;
    }

    case 'scrollbar': {
      const trackTop = r.y;
      const height = r.height;
      const thumb = Math.max(1, Math.round(height * 0.3));
      const offset = Math.round((height - thumb) * (opts.progress.percent / 100));
      for (let i = 0; i < height; i++) {
        const inThumb = i >= offset && i < offset + thumb;
        stamp.char(r.x, trackTop + i, inThumb ? '█' : '│');
      }
      break;
    }

    case 'table': {
      renderTable(stamp, env, r, opts);
      break;
    }
  }

  return stamp.list();
}

function renderTable(stamp: Stamp, env: ToolEnv, r: Rect, opts: WidgetOptions): void {
  if (r.width < 5 || r.height < 3) return;
  const weight = styleWeight(opts.lineStyle);
  const widths = tableColumnWidths(opts.columns, r.width);
  const planner = new BoxPlanner((x, y) => env.read(x, y).char, opts.lineStyle === 'rounded');

  planner.addRect(r.x, r.y, r.width, r.height, weight);
  // Header rule.
  const headerRowY = r.y + 2;
  if (headerRowY < r.y + r.height - 1) {
    planner.addHorizontal(r.x, r.x + r.width - 1, headerRowY, weight);
  }
  // Column rules.
  let cx = r.x;
  const columnX: number[] = [];
  widths.forEach((w, i) => {
    cx += w + (i === 0 ? 0 : 1) + 2;
    if (i < widths.length - 1 && cx < r.x + r.width - 1) {
      columnX.push(cx);
      planner.addVertical(cx, r.y, r.y + r.height - 1, weight);
    }
  });

  for (const { x, y, char } of planner.resolve()) stamp.char(x, y, char);

  // Header labels.
  let textX = r.x + 1;
  opts.columns.forEach((label, i) => {
    stamp.text(textX + 1, r.y + 1, label, widths[i]);
    textX += widths[i] + 3;
  });

  // Body rows.
  const firstBodyY = headerRowY + 1;
  const bodyRows = r.y + r.height - 1 - firstBodyY;
  for (let row = 0; row < Math.min(bodyRows, opts.items.length); row++) {
    let bx = r.x + 1;
    const cells = opts.items[row].split('|').map((s) => s.trim());
    opts.columns.forEach((_, i) => {
      const value = cells[i] ?? (i === 0 ? opts.items[row] : '');
      stamp.text(bx + 1, firstBodyY + row, value, widths[i]);
      bx += widths[i] + 3;
    });
  }
}

export const widgetTool: Tool = {
  id: 'widget',
  name: 'Widget',
  group: 'insert',
  shortcut: 'W',
  hint: 'Drag out a panel, table, progress bar or other TUI widget.',
  plan(stroke: ToolStroke, env: ToolEnv): CellWrite[] {
    const r = rectFromPoints(stroke.start, stroke.current);
    return renderWidget(env, r, env.brush.widgetOptions);
  },
};

/** Standalone helper for templates and the component library. */
export function stampWidget(
  read: (x: number, y: number) => Cell,
  width: number,
  height: number,
  r: Rect,
  opts: WidgetOptions,
  brush: ToolEnv['brush'],
): CellWrite[] {
  const env: ToolEnv = {
    doc: { width, height } as ToolEnv['doc'],
    layer: null as unknown as ToolEnv['layer'],
    selection: {
      allows: () => true,
      getBounds: () => null,
    } as unknown as ToolEnv['selection'],
    brush,
    read,
    readComposite: read,
  };
  return renderWidget(env, r, opts);
}

export const EMPTY_CELL_FALLBACK: Cell = { char: '', fg: TRANSPARENT, bg: TRANSPARENT, attrs: 0 };
